import {
  AfterViewInit,
  Component,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions,
} from '@shared/components';
import { DropdownComponent, DropdownOptions } from '@shared/components/dropdown/dropdown.component';
import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { HTTPMethods, ModalTypes } from '@core/enums';
import { assets } from '@core/constants';
import { VaccinesSearchComponent } from '@shared/components/dropdown/search/vaccines-search/vaccines-search.component';
import { VaccinationCoding } from '@core/classes/other/vaccination-coding';
import { VaccinationEuvac, VaccinationEuvacDisease } from '@core/classes/tables/vaccination-euvac';
import { ResponseSingle } from '@core/classes/other/response';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { checkDate } from '@core/helpers';
import moment from 'moment';

interface DiseaseCoding {
  coding_id: number;
  code?: string;
  description?: string;
  epSOS_OID?: string;
  version?: string;
}

@UntilDestroy()
@Component({
  selector: 'ncp-add-vaccination',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
  ],
  templateUrl: './add-vaccination.component.html',
  styleUrl: './add-vaccination.component.scss',
})
export class AddVaccinationComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('vFrm') protected vaccinationsFormEl?: NgForm;
  @ViewChild('addVaccinationButton') public addVaccinationButton!: ButtonComponent;
  @ViewChild('vaccinationDesc') public vaccinationDesc!: DropdownComponent<VaccinationCoding>;
  @ViewChild('diseaseDropdown') public diseaseDropdown!: DropdownComponent<DiseaseCoding>;
  @ViewChild('vaccineDate') public vaccineDate!: DateComponent;
  @ViewChild('nextVaccineDate') public nextVaccineDate!: DateComponent;
  @ViewChild('vaccinesTemplate') public vaccinesTemplate!: TemplateRef<VaccinationCoding>;
  @ViewChild('diseasesTemplate') public diseasesTemplate!: TemplateRef<DiseaseCoding>;

  private readonly translate = inject(TranslateService);
  private readonly apiService = inject(APIService);
  private readonly patientService = inject(PatientService);

  protected translations: Translation = this.translate.instant('patient.ps.vaccinations');
  protected data = model<any>();
  protected readonly assets = assets;
  protected selectedDiseases = signal<VaccinationEuvacDisease[]>([]);

  protected form: FormGroup = new FormGroup(
    {
      vaccination_id: new FormControl<number | string | undefined>(undefined, [
        Validators.required,
      ]),
      vaccination_date: new FormControl<string>('', [Validators.required]),
      vaccination_reason: new FormControl<string | null>(null),
      vaccination_status: new FormControl<string | null>(null),
      series_number: new FormControl<number | null>(null),
      batch_number: new FormControl<string | null>(null),
      vaccination_next_date: new FormControl<string | null>(null),
      presence_adverse_event: new FormControl<string | null>(null),
      reported_adverse_event: new FormControl<string | null>(null),
      adverse_event_code: new FormControl<string | null>(null),
      observations: new FormControl<string | null>(null),
      note: new FormControl<string | null>(null),
    },
    {
      validators: [this.formValidator()],
    }
  );

  protected btnVacOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_vaccination',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      title: 'add_vaccination',
      options: {
        animation: true,
        size: 'xl',
        fullscreen: 'lg',
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {
        this.onFormReset();
      },
    })
  );

  protected vaccinationDescDDOptions = signal<DropdownOptions<VaccinationCoding>>(
    new DropdownOptions<VaccinationCoding>({
      id: 'vaccinationDescription',
      name: 'vaccinationDescription',
      placeholder: 'vaccination_description',
      dataId: 'id',
      label: {
        id: 'vaccination_desc_label',
        text: 'vaccination_description',
      },
      api: {
        description: {
          path: '/get-vaccines_by_description',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: '',
          },
        },
        brandName: {
          path: '/get-vaccines_by_brand_name',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: '',
          },
        },
      },
      required: true,
      searchComponent: VaccinesSearchComponent,
      formControl: this.form.get('vaccination_id') as FormControl,
    })
  );

  /*
   * ΠΡΟΣΟΧΗ:
   * Το configuration του disease API είναι προσωρινό.
   * Πρέπει να βάλουμε εδώ το πραγματικό endpoint/search component
   * που χρησιμοποιεί το project για disease codings.
   */
  protected diseaseDDOptions = signal<DropdownOptions<DiseaseCoding>>(
    new DropdownOptions<DiseaseCoding>({
      id: 'disease',
      name: 'disease',
      placeholder: 'Select disease',
      dataId: 'coding_id',
      label: {
        id: 'disease_label',
        text: 'Disease',
      },
    })
  );

  protected vaccinationDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'vaccinationDate',
      label: {
        text: 'vaccination_date',
      },
      name: 'vaccinationDate',
      initialValue: '',
      formControl: this.form.get('vaccination_date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  protected vaccinationNextDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'vaccinationNextDate',
      label: {
        text: 'vaccination_next_date',
      },
      name: 'vaccinationNextDate',
      initialValue: '',
      formControl: this.form.get('vaccination_next_date') as FormControl,
      required: false,
    })
  );

  ngOnInit(): void {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.translations = this.translate.instant('patient.ps.vaccinations');
    });

    this.btnVacOptions().onClick = () => {
      this.mod.options().onReset = this.onFormReset;

      const vaccinesOptions = this.vaccinationDescDDOptions();
      vaccinesOptions.itemTemplate = this.vaccinesTemplate;
      this.vaccinationDescDDOptions.set(vaccinesOptions);

      const diseasesOptions = this.diseaseDDOptions();
      diseasesOptions.itemTemplate = this.diseasesTemplate;
      this.diseaseDDOptions.set(diseasesOptions);

      this.vaccinationDesc.detectChanges();
      this.diseaseDropdown.detectChanges();
      this.addVaccinationButton.blur();
      this.mod.open();
    };

    this.modOptions().onSave = this.onSave;
  }

  ngAfterViewInit(): void {}

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      const vaccinationId = (control as FormGroup).controls['vaccination_id'];
      const vaccinationDate = (control as FormGroup).controls['vaccination_date'];
      const patientDOB = moment(
        this.patientService.data?.dob,
        ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'],
        true
      );
      const today = moment();

      if (vaccinationId.value === undefined || vaccinationId.value === null) {
        vaccinationId.setErrors({ v_vaccinationIdInvalid: true });
        errors['v_vaccinationIdInvalid'] = true;
      }

      if (!checkDate(vaccinationDate.value).isValid()) {
        vaccinationDate.setErrors({ v_vaccinationDateInvalid: true });
        errors['v_vaccinationDateInvalid'] = true;
      } else {
        if (checkDate(vaccinationDate.value).isBefore(patientDOB)) {
          vaccinationDate.setErrors({ v_vaccinationDateAfterDOB: true });
          errors['v_vaccinationDateAfterDOB'] = true;
        } else if (checkDate(vaccinationDate.value).isAfter(today)) {
          vaccinationDate.setErrors({ v_vaccinationDateBeforeToday: true });
          errors['v_vaccinationDateBeforeToday'] = true;
        } else {
          vaccinationDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  protected addDisease = () => {
    const disease = this.diseaseDDOptions().value;

    if (!disease) {
      return;
    }

    if (
      this.selectedDiseases().some((selected) => selected.disease_coding_id === disease.coding_id)
    ) {
      this.diseaseDropdown.clearSelection();
      return;
    }

    this.selectedDiseases.update((diseases) => [
      ...diseases,
      {
        disease_coding_id: disease.coding_id,
        code: disease.code,
        description: disease.description,
        epSOS_OID: disease.epSOS_OID,
        version: disease.version,
      },
    ]);

    this.diseaseDropdown.clearSelection();
  };

  protected removeDisease = (diseaseCodingId: number) => {
    this.selectedDiseases.update((diseases) =>
      diseases.filter((disease) => disease.disease_coding_id !== diseaseCodingId)
    );
  };

  protected onSave = () => {
    this.vaccinationsFormEl?.ngSubmit.emit();
  };

  protected validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    });
    return true;
  };

  protected onFormReset = () => {
    this.form.reset();
    this.selectedDiseases.set([]);
    this.vaccinationDesc.clearSelection();
    this.diseaseDropdown.clearSelection();
    this.vaccineDate.reset();
    this.nextVaccineDate.reset();
    this.modOptions().errors = [];
  };

  protected onFormSubmit = () => {
    if (this.validate() && this.form.invalid) {
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;

    const body = {
      vaccination_id: this.vaccinationDescDDOptions().value!.id,
      vaccination_date: this.form.get('vaccination_date')?.value,
      status: 1,
      vaccination_reason: this.nullIfEmpty(this.form.get('vaccination_reason')?.value),
      vaccination_status: this.nullIfEmpty(this.form.get('vaccination_status')?.value),
      series_number: this.form.get('series_number')?.value ?? null,
      batch_number: this.nullIfEmpty(this.form.get('batch_number')?.value),
      vaccination_next_date: this.nullIfEmpty(this.form.get('vaccination_next_date')?.value),
      presence_adverse_event: this.nullIfEmpty(this.form.get('presence_adverse_event')?.value),
      reported_adverse_event: this.nullIfEmpty(this.form.get('reported_adverse_event')?.value),
      adverse_event_code: this.nullIfEmpty(this.form.get('adverse_event_code')?.value),
      observations: this.nullIfEmpty(this.form.get('observations')?.value),
      note: this.nullIfEmpty(this.form.get('note')?.value),
      diseases: this.selectedDiseases().map((disease) => ({
        disease_coding_id: disease.disease_coding_id,
      })),
    };

    this.apiService
      .call({
        path: '/vaccinations-euvac',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.POST,
        body: body,
      })
      ?.subscribe({
        next: (res: ResponseSingle<VaccinationEuvac>) => {
          this.modOptions().disableOnSave = false;

          if (res && res.status?.toString() === '1') {
            const data = this.data()?.tblOptions().data;
            data.unshift(res.data);

            if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
              this.data()!.tbl!.tableData! = data;
            }

            this.mod.close();
          }
        },
        error: () => {
          this.modOptions().disableOnSave = false;
        },
      });
  };

  private nullIfEmpty(value: any): any {
    return value === '' || value === undefined ? null : value;
  }
}
